import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Label } from "@radix-ui/react-label";
import { Combobox } from "@/shared/components/ui/combobox";
import { COMMON_CAR_MAKES } from "@/shared/constants/constants";

interface VehicleEstimateFormProps {
  onEstimate: (data: {
    year: number;
    make: string;
    model: string;
    condition: "excellent" | "good" | "fair" | "poor";
    mileage?: number;
  }) => void;
  isLoading: boolean;
}

export function VehicleEstimateForm({ onEstimate, isLoading }: VehicleEstimateFormProps) {
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [customMake, setCustomMake] = useState("");
  const [model, setModel] = useState("");
  const [condition, setCondition] = useState<"excellent" | "good" | "fair" | "poor" | "">("");
  const [mileage, setMileage] = useState("");

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  // Convert car makes to combobox options
  const makeOptions = COMMON_CAR_MAKES.map((carMake) => ({
    value: carMake,
    label: carMake,
  }));

  const isOtherMake = make === "Other";
  const finalMake = isOtherMake ? customMake : make;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (year && finalMake && model && condition) {
      onEstimate({
        year: parseInt(year),
        make: finalMake.trim(),
        model: model.trim(),
        condition,
        mileage: mileage ? parseInt(mileage.replace(/,/g, "")) : undefined,
      });
    }
  };

  const isFormValid = year && finalMake.trim() && model.trim() && condition;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-4 mt-2">
        <Label htmlFor="year" className="text-sm font-light text-stone-800 block mb-2">
          Year
        </Label>
        <Select value={year} onValueChange={setYear} disabled={isLoading}>
          <SelectTrigger className="text-sm">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={y.toString()} className="text-sm">
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4">
        <Label htmlFor="make" className="text-sm font-light text-stone-800 block mb-2">
          Make
        </Label>
        <div className="space-y-2">
          <Combobox
            options={makeOptions}
            value={make}
            onValueChange={(value) => {
              setMake(value);
              if (value !== "Other") {
                setCustomMake("");
              }
            }}
            placeholder="Select make"
            searchPlaceholder="Search"
            emptyText="No results found"
            disabled={isLoading}
            allowCustom={true}
            customOptionValue="Other"
            customOptionLabel="Add custom make"
          />
          {isOtherMake && (
            <Input
              type="text"
              placeholder="Enter custom make"
              value={customMake}
              onChange={(e) => setCustomMake(e.target.value)}
              disabled={isLoading}
              className="text-sm"
            />
          )}
        </div>
      </div>

      <div className="mb-4">
        <Label htmlFor="model" className="text-sm font-light text-stone-800 block mb-2">
          Model
        </Label>
        <Input
          id="model"
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="e.g., Model 3, Civic, F-150"
          disabled={isLoading}
        />
      </div>

      <div className="mb-4">
        <Label htmlFor="condition" className="text-sm font-light text-stone-800 block mb-2">
          Condition
        </Label>
        <Select
          value={condition}
          onValueChange={(value) => setCondition(value as "excellent" | "good" | "fair" | "poor")}
          disabled={isLoading}
        >
          <SelectTrigger className="text-sm">
            <SelectValue placeholder="Select condition" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="excellent" className="text-sm">
              Excellent
            </SelectItem>
            <SelectItem value="good" className="text-sm">
              Good
            </SelectItem>
            <SelectItem value="fair" className="text-sm">
              Fair
            </SelectItem>
            <SelectItem value="poor" className="text-sm">
              Poor
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4">
        <Label htmlFor="mileage" className="text-sm font-light text-stone-800 block mb-2">
          Mileage <span className="text-stone-400 text-sm">(Optional)</span>
        </Label>
        <Input
          id="mileage"
          type="text"
          value={mileage}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, "");
            setMileage(value ? parseInt(value).toLocaleString() : "");
          }}
          placeholder="e.g., 35,000"
          disabled={isLoading}
        />
      </div>

      <Button type="submit" disabled={!isFormValid || isLoading} className="w-full">
        Get Estimate
      </Button>
      <div className="h-5"></div>
    </form>
  );
}
